import { AppcuesEventId } from '@upright/ui/integrations/appcues/types';
import { ProductTable } from './table';
import { HierarchyProductTable } from './table/hierarchy-table';
import { PiuProductState, PiuDataState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import { useSingleFirePlgAppcuesTracker } from '@upright/ui/common/upright/model/output/scores/csrd/csrd-company-double-materiality/use-plg-appcues-tracker';
import { SegmentViewMode } from './segment-view-preferences';

interface ProductModificationUIProps {
  companyData: PiuDataState | undefined;
  showMatching: boolean;
  showAttributes: boolean;
  /**
   * List view or Hierarchy view. Optional and defaults to List. Hosts pass the
   * user's choice only with segment-editor access (`useHasProductSegmentsAccess`):
   * the dashboard "Products & services" page, the DD wizard's and the consultant
   * company-creation wizard's review steps, the Edit company dialog, and the chat
   * product set card (PLT-7586).
   */
  viewMode?: SegmentViewMode;
  onProductUpdate: (updatedProduct: PiuProductState) => void;
  duplicateProduct: (product: PiuProductState) => void;
  deleteProduct: (product: PiuProductState) => void;
  /**
   * Enable Hierarchy-view drag-and-drop reparenting (PLT-5690), with segment-editor
   * access and while the host is editing. When unset the Hierarchy view is read-only.
   */
  dndEnabled?: boolean;
  onReparent?: (productId: string, newParentId: string | null) => void;
  /** Hierarchy "Add child product" — open the create dialog with this parent pre-set (PLT-5691). */
  onAddChildProduct?: (parentId: string) => void;
  /** Hierarchy "Add child segment" — create a new empty segment inside this segment (PLT-5799). */
  onAddChildSegment?: (parentId: string) => void;
  /** Hierarchy "Add parent segment" — insert a segment as the PARENT of this node (PLT-5707). */
  onAddParentSegment?: (productId: string) => void;
  disabled: boolean;
  /** PLT-5109: read-only (blanketti) set — hide the per-product edit modal and matching/attributes columns. */
  readOnly?: boolean;
  companyId?: string;
  /**
   * Gated segment editor is on (PLT-5707). Enables the list view's "Segment"
   * tile column + Revenue/Normalized columns and leaf-only rows.
   */
  segmentEditorEnabled?: boolean;
}

export function ProductInputUiMain({
  companyData,
  showMatching,
  showAttributes,
  viewMode,
  onProductUpdate,
  duplicateProduct,
  deleteProduct,
  dndEnabled,
  onReparent,
  onAddChildProduct,
  onAddChildSegment,
  onAddParentSegment,
  disabled,
  readOnly,
  companyId,
  segmentEditorEnabled,
}: ProductModificationUIProps) {
  useSingleFirePlgAppcuesTracker(AppcuesEventId.productListPageViewed);
  if (viewMode === 'hierarchy') {
    // Segments are now first-class editable nodes in `products` (PLT-5690), so
    // the tree renders directly from it — no separate display-only list.
    return (
      <HierarchyProductTable
        products={companyData?.products}
        showMatching={showMatching}
        showAttributes={showAttributes}
        dndEnabled={dndEnabled}
        onReparent={onReparent}
        onAddChildProduct={onAddChildProduct}
        onAddChildSegment={onAddChildSegment}
        onAddParentSegment={onAddParentSegment}
        onProductUpdate={onProductUpdate}
        duplicateProduct={duplicateProduct}
        deleteProduct={deleteProduct}
        disabled={disabled}
        companyId={companyId}
      />
    );
  }
  return (
    <ProductTable
      products={companyData?.products}
      onProductUpdate={onProductUpdate}
      showMatching={showMatching}
      showAttributes={showAttributes}
      duplicateProduct={duplicateProduct}
      deleteProduct={deleteProduct}
      disabled={disabled}
      readOnly={readOnly}
      companyId={companyId}
      showSegmentContext={segmentEditorEnabled}
    />
  );
}
